import React, { useEffect, useState } from 'react';
import { AppFormLabel, AppModal, AppSpin } from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { editRezeptStatus } from '@/services/main/rezept/rezeptListService';
import type { RezeptListItem } from '@/types/rezept.types';
import { warekiMonthToYearmonth } from '@/utils/dateConverter';

interface Props {
  isOpen: boolean;
  rezept: RezeptListItem | null;
  onClose: () => void;
  onUpdated: () => void;
}

// BD: Medix Hitowa_BD_Main Site_Monthly Operations - v1.0 / Edit record's status.
// Status values: 0=未印刷, 1=印刷済, 2=確定.
const STATUS_UNPRINTED = 0;
const STATUS_PRINTED = 1;
const STATUS_CONFIRMED = 2;

const revertLabel = (status: number): string => {
  if (status === STATUS_PRINTED) return '「印刷済」を戻す';
  if (status === STATUS_CONFIRMED) return '「確定」を戻す';
  return '提出年月を変更する';
};

const rezeptKbnLabel = (kbn: number | string | undefined): string => {
  const v = typeof kbn === 'string' ? parseInt(kbn, 10) : (kbn ?? 1);
  return v === 2 ? '（関レセ）' : '（本レセ）';
};

/**
 * Extract numeric YYYYMM from any common year-month representation:
 * "202403" / "2024-03" / "2024/03" / "2024年03月" / "令和06年03月".
 * Returns 0 if unparseable.
 */
const toYearMonthNum = (raw: string | number | null | undefined): number => {
  if (raw === null || raw === undefined || raw === '') return 0;
  const str = String(raw).trim();
  const parsedYearmonth = warekiMonthToYearmonth(str);
  if (parsedYearmonth) {
    const mo = parsedYearmonth % 100;
    if (mo >= 1 && mo <= 12) return parsedYearmonth;
  }
  const m = str.match(/(\d{4})\D*(\d{1,2})/);
  if (!m) return 0;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  if (mo < 1 || mo > 12) return 0;
  return y * 100 + mo;
};

const toDatePickerMonthValue = (raw: string | number | null | undefined): string => {
  const ym = toYearMonthNum(raw);
  if (ym === 0) return '';
  const y = Math.floor(ym / 100);
  const mo = ym % 100;
  return `${y}-${String(mo).padStart(2, '0')}`;
};

/**
 * BD: fallback when 提出年月 is invalid or ≤ 施術年月 → returns `treatement_yearmonth + 1`.
 */
const treatmentYmPlusOne = (treatementYm: string | number | null | undefined): string => {
  const ym = toYearMonthNum(treatementYm);
  if (ym === 0) return '';
  const y = Math.floor(ym / 100);
  const mo = ym % 100;
  const next = new Date(y, mo, 1); // mo (1-12) as 0-indexed month → +1 month
  return `${next.getFullYear()}${String(next.getMonth() + 1).padStart(2, '0')}`;
};

const EditRezeptStatusModal: React.FC<Props> = ({ isOpen, rezept, onClose, onUpdated }) => {
  const toast = useAppToast();
  const { tenant, clinicNumber } = useClinicContext();
  const [rezeptYearmonth, setRezeptYearmonth] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && rezept) {
      setRezeptYearmonth(String(rezept.rezept_yearmonth || ''));
      setErrorMsg(null);
    }
  }, [isOpen, rezept]);

  if (!rezept) return null;

  const status = Number(rezept.status ?? 0);
  const isUnprinted = status === STATUS_UNPRINTED;
  const statusLabel = revertLabel(status);

  const handleApply = async () => {
    if (!rezept) return;
    setErrorMsg(null);

    // BD: status=0 → can change 提出年月; status=1|2 → revert one step.
    const payload = isUnprinted
      ? { action: 'update_yearmonth' as const, rezept_yearmonth: rezeptYearmonth }
      : { action: 'revert_status' as const };

    if (isUnprinted && !rezeptYearmonth) {
      // BD TC4: client-side empty check must fire both inline error and flash toast.
      setErrorMsg('【提出年月】を入力してください');
      toast.error('更新ができませんでした。');
      return;
    }

    setSubmitting(true);
    try {
      await editRezeptStatus(tenant, clinicNumber, rezept.id, payload);
      toast.success('更新が正常に終了しました。');
      onUpdated();
      onClose();
    } catch (err) {
      // BD: show the specific BE error message if present; default to "更新ができませんでした。"
      const beMsg = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      setErrorMsg(beMsg || '更新ができませんでした。');
      toast.error('更新ができませんでした。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="レセプト編集"
      maxWidth={520}
      onOk={handleApply}
      okText="適用"
      okButtonProps={{ loading: submitting, icon: undefined }}
    >
      <AppSpin spinning={submitting}>
        <div className="space-y-4 py-2">
          {errorMsg && (
            <div className="px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded">
              {errorMsg}
            </div>
          )}

          {/* BD §1 + §2 — Format: {patient_informations.name}　{（本レセ）|（関レセ）} */}
          <div className="text-base font-medium text-gray-900">
            {`${rezept.patient_name ?? ''}　${rezeptKbnLabel(rezept.rezept_kbn)}`}
          </div>

          {/* HIT-4287 — Status label: red, horizontally centered.
              status=0 (未印刷) shows 提出年月を変更する; status=1/2 shows revert label. */}
          <div className="text-sm text-red-600 text-center">{statusLabel}</div>

          {/* HIT-4287: 提出年月 row centered horizontally. */}
          <div className="flex items-center justify-center gap-3 text-sm">
            <AppFormLabel className="whitespace-nowrap min-w-[80px]" label="提出年月" />
            <RA2NDatePicker
              picker="month"
              value={toDatePickerMonthValue(rezeptYearmonth) || undefined}
              onChange={v => {
                // BD TC16/17: if input is invalid (datepicker fires '' when commitRawInput fails)
                // or 提出年月 ≤ 施術年月 → auto fallback = 施術年月 + 1 month.
                const treatYm = toYearMonthNum(rezept.treatement_yearmonth);
                const newYm = toYearMonthNum(v);
                if (newYm === 0 || (treatYm > 0 && newYm <= treatYm)) {
                  const fallback = treatmentYmPlusOne(rezept.treatement_yearmonth);
                  setRezeptYearmonth(fallback);
                } else {
                  setRezeptYearmonth(String(newYm));
                }
              }}
              placeholder="年月指定"
              disabled={!isUnprinted}
            />
          </div>
        </div>
      </AppSpin>
    </AppModal>
  );
};

export default EditRezeptStatusModal;
